import type { MessageDescriptor } from '@lingui/core'

import { OrderClass, OrderKind, SigningScheme } from '@cowprotocol/cow-sdk'
import { Currency, CurrencyAmount, Price, Token } from '@cowprotocol/currency'

import { msg } from '@lingui/core/macro'
import BigNumber from 'bignumber.js'
import JSBI from 'jsbi'

import { Order, OrderStatus } from 'legacy/state/orders/actions'

import { ComposableCowInfo } from 'common/types'

import { getOrderExecutedAmounts } from './getOrderExecutedAmounts'
import { getOrderFilledAmount } from './getOrderFilledAmount'
import { getOrderSurplus } from './getOrderSurplus'
import { isOrderFilled } from './isOrderFilled'
import { isPartiallyFilled } from './isPartiallyFilled'

export interface ParsedOrder {
  /** Indexed EOA TWAP parts bypass the legacy order store. Retain the order for cancellation. */
  cancellationOrder?: Order
  id: string
  owner: string
  isCancelling: boolean | undefined
  isEoaTwapOrder?: boolean
  isSponsored?: boolean
  isNativeSell?: boolean
  isUnfillable?: boolean
  receiver: string | undefined
  inputToken: Token
  outputToken: Token
  kind: OrderKind
  sellAmount: string
  buyAmount: string
  feeAmount: string
  class: OrderClass
  status: OrderStatus
  partiallyFillable: boolean
  creationTime: Date
  expirationTime: Date
  fulfillmentTime: string | undefined
  composableCowInfo?: ComposableCowInfo
  // `getUiOrderType` reads this to tell a Solana limit order from a market one, and reaches it
  // through an optional field — drop it here and every Solana order silently becomes unclassified.
  appData: Order['appData']
  fullAppData: Order['fullAppData']
  signingScheme: SigningScheme
  executionData: ParsedOrderExecutionData
}

export interface ParsedOrderExecutionData {
  executedBuyAmount: JSBI
  executedSellAmount: JSBI
  fullyFilled: boolean
  partiallyFilled: boolean
  filledAmount: BigNumber
  filledPercentage: BigNumber
  surplusAmount: BigNumber
  surplusPercentage: BigNumber
  executedFeeAmount: string | undefined
  filledPercentDisplay: string
  executedPrice: Price<Currency, Currency> | null
  activityId: string | undefined
  activityTitle: MessageDescriptor | string
}

// Solana charges no fee, so its order-book omits `feeAmount` entirely. `transformOrder` in
// sdk-order-book now fills it in, but `addOrUpdateOrders` merges onto an order already in the
// store without overwriting the field — one persisted before that fix keeps `undefined`.
function getFeeAmount(order: Order): string {
  return order.feeAmount ?? '0'
}

export const parseOrder = (order: Order): ParsedOrder => {
  const { amount: filledAmount, percentage: filledPercentage } = getOrderFilledAmount(order)
  const { amount: surplusAmount, percentage: surplusPercentage } = getOrderSurplus(order)
  const { executedBuyAmount, executedSellAmount } = getOrderExecutedAmounts(order)
  const expirationTime = new Date(Number(order.validTo) * 1000)
  const executedFeeAmount = order.apiAdditionalInfo?.executedFeeAmount
  const creationTime = new Date(order.creationTime)
  const fulfillmentTime = order.fulfillmentTime
  const fullyFilled = isOrderFilled(order)
  const partiallyFilled = isPartiallyFilled(order)
  const filledPercentDisplay = filledPercentage.times(100).toString()
  const executedPrice =
    JSBI.greaterThan(executedBuyAmount, JSBI.BigInt(0)) && JSBI.greaterThan(executedSellAmount, JSBI.BigInt(0))
      ? new Price({
          baseAmount: CurrencyAmount.fromRawAmount(order.inputToken, executedSellAmount),
          quoteAmount: CurrencyAmount.fromRawAmount(order.outputToken, executedBuyAmount),
        })
      : null
  const showCreationTxLink =
    (order.status === OrderStatus.CREATING || order.status === OrderStatus.FAILED) &&
    order.orderCreationHash &&
    !order.apiAdditionalInfo
  const activityId = showCreationTxLink ? order.orderCreationHash : order.id
  const activityTitle = showCreationTxLink ? msg`Creation transaction` : msg`Order ID`

  const executionData: ParsedOrderExecutionData = {
    executedBuyAmount,
    executedSellAmount,
    filledAmount,
    filledPercentage,
    filledPercentDisplay,
    surplusAmount,
    surplusPercentage,
    executedFeeAmount,
    executedPrice,
    fullyFilled,
    partiallyFilled,
    activityId,
    activityTitle,
  }

  return {
    id: order.id,
    owner: order.owner,
    isCancelling: order.isCancelling,
    isEoaTwapOrder: order.isEoaTwapOrder,
    isSponsored: order.isSponsored,
    isNativeSell: order.isNativeSell,
    isUnfillable: order.isUnfillable,
    inputToken: order.inputToken,
    outputToken: order.outputToken,
    kind: order.kind,
    sellAmount: order.sellAmount,
    buyAmount: order.buyAmount,
    feeAmount: getFeeAmount(order),
    class: order.class,
    status: order.status,
    partiallyFillable: order.partiallyFillable,
    composableCowInfo: order.composableCowInfo,
    receiver: order.receiver || undefined,
    creationTime,
    expirationTime,
    fulfillmentTime,
    appData: order.appData,
    fullAppData: order.fullAppData,
    executionData,
    signingScheme: order.signingScheme,
  }
}

export function isParsedOrder(order: Order | ParsedOrder): order is ParsedOrder {
  return !!(order as ParsedOrder).executionData
}
